/**
 * AI:
 * Status: "in progress"
 * Type: Service
 * SubType: WidgetConfig
 * Reason: Configures the report/list data provider of the billing-run detail report table
 *         (report bm.run.1), fixed-filtered by the selected billing area and the run status
 *         taken from the runStatus query parameter.
 */
import {MvsCoreService} from "../../../../../core/shared/service/mvs-core.service";
import {MvsCrudService} from "../../../../../core/shared/service/crud/mvs-crud.service";
import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {BillingAreaDto} from "../../../model/dto/entity/billing-area.dto";
import {ActivatedRoute, Router} from "@angular/router";
import {BillingRunService} from "../../../service/api/billing-run.service";
import {forkJoin} from "rxjs";
import {ContractBillingRunStatus} from "../../../enum/contract-billing-run-status.enum";
import {WidgetData} from "../../../../../core/shared/widget/widget-data";
import {MvsFormValueListEntryDto} from "../../../../../core/shared/dto/form/dto/mvs-form-value-list-entry.dto";
import {MvsUiObjectService} from "../../../../../core/shared/object/service/mvs-ui-object.service";
import {DtoTemplate} from "../../../../../core/shared/dto/dto-template";
import {ObjectRequestList} from "../../../../../core/shared/dto/object/request/object-request-list";
import {WidgetFactory} from "../../../../../core/shared/widget/service/widget.factory";
import {FilterCriteria} from "../../../../../core/shared/filter/api/filter.criteria";
import {ObjectIdentifierData} from "../../../../../core/shared/basic/object-identifier-data";
import {ObjectRequestListGroupBy} from "../../../../../core/shared/dto/object/request/object-request-list-group-by";
import {ObjectRequestListAttribute} from "../../../../../core/shared/dto/object/request/object-request-list-attribute";
import {
    DtoListAttributeRequestAggregateEnum
} from "../../../../../core/shared/dto/enum/dto-list-attribute-request-aggregate.enum";
import {
    ObjectRequestEntityProcessingTypeEnum
} from "../../../../../core/shared/dto/object/request/object-request-entity-processing-type.enum";
import {SelectChangeEvent} from "primeng/select";

@Component({
    selector: 'bm-billing-run-detailed-report-component',
    templateUrl: 'bm-billing-run-detailed-report.component.html',
    standalone: false
})
export class BmBillingRunDetailedReportComponent implements OnInit, OnChanges, OnDestroy {

    billingAreaService: MvsCrudService;
    busy: boolean;
    initialized: boolean;

    billingRunReportWidget: WidgetData;
    billingAreaList: BillingAreaDto[];
    selectedBillingArea: BillingAreaDto;
    statusEntries: MvsFormValueListEntryDto[];
    selectedStatus: number;
    totalCount: number;


    constructor(
        protected coreService: MvsCoreService,
        
        protected objectService: MvsUiObjectService,
        protected billingRunService: BillingRunService,
        protected router: Router,
        protected route: ActivatedRoute) {
        this.billingAreaService = this.coreService.getCrudService("bm.BillingArea");

    }

    ngOnInit(): void {
        this.initComponent();

    }

    initComponent() {
        this.retrieveBillingAreas();
    }


    refreshComponent() {

        this.route.queryParamMap?.subscribe(params => {
            const areaStatus = params.get('runStatus');

            if (areaStatus) {
                this.selectedStatus = Number(areaStatus);
                this.refreshReportWidget();
            } else {
                this.selectedStatus = null;
                this.refreshReportWidget();
            }

            this.initialized = true;

        });

    }

    retrieveBillingAreas() {
        forkJoin({
            template: this.billingRunService.template(new DtoTemplate()),
            billingAreas: this.billingAreaService.list(ObjectRequestList.createBasic(true, [], []))
        }).subscribe(({template, billingAreas}) => {
            this.statusEntries = template.getFormField('status')?.valueList?.entries || [];

            const allEntry = new BillingAreaDto();
            allEntry.name = 'All';
            allEntry.id = -1;

            this.billingAreaList = [allEntry, ...billingAreas.entries];
            this.selectedBillingArea = allEntry;

            this.getStatusCount();
        });
    }

    refreshReportWidget() {

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: Report table (bm.run.1) of the billing runs with their success/warning/error counts per
         *   billing area and status; a count-cell click drills down into that run
         */
        this.billingRunReportWidget = WidgetFactory.createWidget(
            "bm.billing.run.detailed.dashboard.report.widget",
            "Abrechnungsläufe im Detail",
            "table",
            "list",
            "report",
            "bm.run.1");

        this.billingRunReportWidget.dataProviderListRequest = this.getObjectRequestList();
    }

    getObjectRequestList(): ObjectRequestList {

        let filterCriteria: FilterCriteria[] = [];

        if (this.selectedBillingArea && this.selectedBillingArea.id != -1) {
            filterCriteria.push(FilterCriteria.create('area', FilterCriteria.cOperatorEqual, this.selectedBillingArea.name));
        }

        if (this.selectedStatus != null) {
            filterCriteria.push(FilterCriteria.create('status', FilterCriteria.cOperatorEqual, this.selectedStatus));
        }

        return ObjectRequestList.createBasic(
            true,
            filterCriteria,
            []);
    }

    handleBillingAreaChange(event: SelectChangeEvent) {
        this.getStatusCount();
    }

    handleObjectSelect(object: ObjectIdentifierData) {

        const field = object.field;

        const statusMap: { [key: string]: ContractBillingRunStatus } = {
            'success_count': ContractBillingRunStatus.SUCCESS,
            'error_count': ContractBillingRunStatus.ERRORS,
            'warning_count': ContractBillingRunStatus.WARNINGS
        };

        const statusEnumValue = statusMap[field?.id];

        if (statusEnumValue !== undefined) {
            this.router.navigate([object.objectId], {
                relativeTo: this.route,
                queryParams: {
                    contractStatus: statusEnumValue,
                    navItem: 'dashboard'
                }
            });
        } else {
            this.router.navigate([object.objectId], {
                relativeTo: this.route,
                queryParams: {
                    navItem: 'dashboard'
                }
            });
        }
    }

    onStatusClick(statusKey?: number) {
        this.selectedStatus = statusKey;
        this.router.navigate([], {
            queryParams: {runStatus: statusKey},
            queryParamsHandling: 'merge',
        });
    }

    getStatusCount() {
        this.totalCount = 0;

        const filters: FilterCriteria[] = this.selectedBillingArea?.id !== -1
            ? [FilterCriteria.create('billingArea', FilterCriteria.cOperatorEqual, this.selectedBillingArea.id)]
            : [];

        const objectRequestList = ObjectRequestListGroupBy.create(
            false,
            filters,
            [],
            ['status'],
            [new ObjectRequestListAttribute('status', 'count', DtoListAttributeRequestAggregateEnum.count)],
            ObjectRequestEntityProcessingTypeEnum.regular
        );

        this.billingRunService.groupBy(objectRequestList).subscribe(groupBy => {
            const grouped = groupBy.entries;

            this.statusEntries.forEach(entry => {
                entry['uiSelected'] = false;
                const match = grouped.find(g => g['status'] === entry.key);
                entry['count'] = match?.['status_count'] || 0;
                this.totalCount += entry['count'];
            });

            this.refreshComponent();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (!this.initialized) {
            return;
        }

        if (changes["id"]) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {

    }
}
